import React, { useState, useEffect } from 'react';
import {
  Shield, CheckCircle, XCircle, AlertTriangle, FileText, Download,
  ChevronDown, ChevronUp, Lock, Eye, Clock, BarChart3
} from 'lucide-react';
import { getComplianceFrameworks, getSOC2Report, getISO27001Report } from '../services/api';

const statusConfig = {
  pass: { color: '#34C759', icon: CheckCircle, label: 'PASS' },
  partial: { color: '#FFCC00', icon: AlertTriangle, label: 'PARTIAL' },
  fail: { color: '#FF3B30', icon: XCircle, label: 'FAIL' },
};

const ComplianceDashboard = () => {
  const [frameworks, setFrameworks] = useState(null);
  const [activeFramework, setActiveFramework] = useState('soc2');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [expandedControl, setExpandedControl] = useState(null);

  useEffect(() => { fetchFrameworks(); }, []);
  useEffect(() => { fetchReport(activeFramework); }, [activeFramework]);

  const fetchFrameworks = async () => {
    try {
      const resp = await getComplianceFrameworks();
      setFrameworks(resp.data);
    } catch (err) { console.error('Failed to load frameworks', err); }
    finally { setLoading(false); }
  };

  const fetchReport = async (fw) => {
    setReportLoading(true);
    try {
      const resp = fw === 'soc2' ? await getSOC2Report() : await getISO27001Report();
      setReport(resp.data);
    } catch (err) { console.error('Failed to load report', err); }
    finally { setReportLoading(false); }
  };

  const exportReport = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.framework.replace(/\s+/g, '-').toLowerCase()}-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-[#9CA3AF]">Loading compliance data...</div></div>;
  }

  const getScoreColor = (score) => {
    if (score >= 90) return '#34C759';
    if (score >= 70) return '#FFCC00';
    return '#FF3B30';
  };

  // Group controls by category
  const groupedControls = report?.controls?.reduce((acc, ctrl) => {
    const cat = ctrl.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ctrl);
    return acc;
  }, {}) || {};

  return (
    <div data-testid="compliance-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Shield className="w-6 h-6 text-[#0066FF]" />
            <h1 className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Compliance Dashboard
            </h1>
          </div>
          <p className="text-sm text-[#6B7280]">
            Auto-generated evidence for SOC 2 Type II and ISO 27001 Annex A controls
          </p>
        </div>
        {report && (
          <button onClick={exportReport} className="secondary-btn text-sm flex items-center gap-2" data-testid="export-compliance-btn">
            <Download className="w-4 h-4" /> Export Report
          </button>
        )}
      </div>

      {/* Framework Cards */}
      {frameworks && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {frameworks.frameworks.map((fw) => {
            const isActive = (fw.id === 'soc2' && activeFramework === 'soc2') || (fw.id === 'iso27001' && activeFramework === 'iso27001');
            const scoreColor = getScoreColor(fw.score);
            return (
              <button
                key={fw.id}
                onClick={() => setActiveFramework(fw.id)}
                className={`enterprise-card p-5 text-left transition-all ${isActive ? 'border-l-4' : 'opacity-70 hover:opacity-100'}`}
                style={isActive ? { borderLeftColor: scoreColor } : {}}
                data-testid={`framework-${fw.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[#F9FAFB] font-semibold">{fw.name}</div>
                    <div className="text-[10px] text-[#6B7280] uppercase mt-0.5">
                      {fw.controls_total} controls &middot; {fw.status}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-mono font-bold" style={{ color: scoreColor }}>{fw.score}%</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-[#34C759]" />
                    <span className="text-xs text-[#9CA3AF]">{fw.controls_passed} passed</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-[#FFCC00]" />
                    <span className="text-xs text-[#9CA3AF]">{fw.controls_partial} partial</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-[#FF3B30]" />
                    <span className="text-xs text-[#9CA3AF]">{fw.controls_failed} failed</span>
                  </div>
                </div>
                {/* Mini progress bar */}
                <div className="flex h-1.5 mt-3 bg-[#1A1A1A] overflow-hidden">
                  <div className="bg-[#34C759]" style={{ width: `${(fw.controls_passed / fw.controls_total) * 100}%` }} />
                  <div className="bg-[#FFCC00]" style={{ width: `${(fw.controls_partial / fw.controls_total) * 100}%` }} />
                  <div className="bg-[#FF3B30]" style={{ width: `${(fw.controls_failed / fw.controls_total) * 100}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Report Detail */}
      {reportLoading ? (
        <div className="enterprise-card p-12 text-center text-[#9CA3AF]">Generating compliance report...</div>
      ) : report && (
        <div className="space-y-4">
          {/* Report Header */}
          <div className="enterprise-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[#F9FAFB] font-semibold text-base">{report.framework}</div>
                <div className="text-xs text-[#6B7280] font-mono mt-1">
                  Report ID: {report.report_id} &middot; Generated: {report.generated_at?.slice(0, 19).replace('T', ' ')} UTC
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-lg font-mono font-bold text-[#34C759]">{report.summary.passed}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase">Passed</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-mono font-bold text-[#FFCC00]">{report.summary.partial}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase">Partial</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-mono font-bold text-[#FF3B30]">{report.summary.failed}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase">Failed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Evidence Stats */}
          {report.evidence_stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[
                { label: 'Identities', value: report.evidence_stats.total_agents, color: '#0066FF' },
                { label: 'Policies', value: report.evidence_stats.active_policies, color: '#34C759' },
                { label: 'Audit Events', value: report.evidence_stats.total_audit_events, color: '#F9FAFB' },
                { label: 'Connectors', value: report.evidence_stats.connectors_count, color: '#0066FF' },
                { label: 'Violations', value: report.evidence_stats.total_violations, color: '#FFCC00' },
                { label: 'Gate Reviews', value: report.evidence_stats.deployment_reviews, color: '#34C759' },
              ].map((m, i) => (
                <div key={i} className="enterprise-card p-3 text-center">
                  <div className="text-lg font-mono font-bold" style={{ color: m.color }}>{m.value}</div>
                  <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Controls by Category */}
          {Object.entries(groupedControls).map(([category, controls]) => (
            <div key={category} className="enterprise-card">
              <div className="p-4 border-b border-[#27272A]/40">
                <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider">{category}</h3>
              </div>
              <div className="divide-y divide-[#27272A]/30">
                {controls.map((ctrl) => {
                  const st = statusConfig[ctrl.status] || statusConfig.partial;
                  const isExp = expandedControl === ctrl.id;
                  return (
                    <div key={ctrl.id} data-testid={`control-${ctrl.id}`}>
                      <div
                        className="p-4 cursor-pointer hover:bg-[#1A1A1A]/30 transition-colors"
                        onClick={() => setExpandedControl(isExp ? null : ctrl.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <st.icon className="w-4 h-4 shrink-0" style={{ color: st.color }} />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-[#6B7280]">{ctrl.id}</span>
                                <span className="text-sm text-[#F9FAFB] font-medium">{ctrl.title}</span>
                              </div>
                              <div className="text-xs text-[#6B7280] mt-0.5 max-w-2xl">{ctrl.description}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[10px] font-mono uppercase px-2 py-0.5" style={{ color: st.color, backgroundColor: `${st.color}15` }}>
                              {st.label}
                            </span>
                            <span className="text-[10px] font-mono text-[#6B7280]">
                              {ctrl.auto_evidence_count} evidence
                            </span>
                            {isExp ? <ChevronUp className="w-4 h-4 text-[#6B7280]" /> : <ChevronDown className="w-4 h-4 text-[#6B7280]" />}
                          </div>
                        </div>
                      </div>
                      {isExp && (
                        <div className="px-4 pb-4 ml-7">
                          <div className="p-3 bg-[#0A0A0A] border border-[#0066FF]/20">
                            <div className="text-[10px] text-[#0066FF] uppercase tracking-wider mb-2">Auto-Generated Evidence</div>
                            <ul className="space-y-1.5">
                              {ctrl.evidence.map((e, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-[#9CA3AF]">
                                  <CheckCircle className="w-3 h-3 text-[#34C759] mt-0.5 shrink-0" />
                                  <span>{e}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;
